// @ts-nocheck
import * as React from "react";
import { ManualShell, Section, Lead, Steps, Note, GoTo, Shot, FlowStrip } from "./manual-shell";
import { supportPhone } from "./common";

// 실제 화면 스크린샷 경로 — playwright test e2e/manual-shots/system.gen.spec.ts 로
// 생성(public/manual-shots/system/).
const S = (key) => `/manual-shots/system/${key}.png`;

// 시스템 관리자 사용자 매뉴얼 — 인앱 가이드.
// 진입점은 플랫폼 대시보드 Topbar 우측 상단의 "사용자 매뉴얼" 버튼(dashboard-platform.tsx,
// user.role === 'system_admin' 일 때만 조건부 노출).
// 콘텐츠는 system_admin 의 실제 전용 화면·전역 권한·마스터 CRUD·RBAC/감사/설정에 맞춰 작성했다.
// 공용 셸/프리미티브는 manual-shell.tsx 에서 가져온다.

const TOC = [
  { id: "start",     label: "1. 시작하기" },
  { id: "dashboard", label: "2. 플랫폼 대시보드" },
  { id: "regions",   label: "3. 권역 관리" },
  { id: "orgs",      label: "4. 기관 마스터" },
  { id: "managers",  label: "5. 케어매니저 관리" },
  { id: "admins",    label: "6. 관리자 관리" },
  { id: "settings",  label: "7. 권한 매트릭스·시스템 설정" },
  { id: "audit",     label: "8. 감사 로그" },
  { id: "ops",       label: "9. 승인·매칭 운영" },
  { id: "security",  label: "10. 보안·운영 주의" },
  { id: "faq",       label: "11. 자주 묻는 질문 · 문의" },
];

const INTRO = (
  <>
    이 매뉴얼은 시스템 관리자가 플랫폼 전체를 운영·관리하는 업무를 화면 흐름에 맞춰 안내합니다.
    권역·기관·케어매니저 마스터 데이터 관리, 관리자 계정과 권한 매트릭스(RBAC), 감사 로그까지
    플랫폼 운영 전반을 다룹니다. 시스템 관리자는 <b>권역 격리가 없는 전역 슈퍼유저</b>이며,
    여기서 설명하는 관리자·감사·설정 화면은 시스템 관리자 <b>전용</b>입니다(10장 참고).
  </>
);

function SystemManual() {
  return (
    <ManualShell
      role="platform"
      topbarTitle="사용자 매뉴얼"
      topbarSub="시스템 관리자 가이드"
      badge="SYSTEM ADMIN GUIDE"
      title="시스템 관리자 사용자 매뉴얼"
      revised="최종 개정 2026-06-18 · 시스템 관리자(system_admin) 기준 · 화면 캡처 최신화"
      intro={INTRO}
      toc={TOC}
    >
      <Section id="start" title="1. 시작하기">
        <Lead>시스템 관리자는 플랫폼 전체를 책임지는 <b>전역 슈퍼유저</b>입니다. 권역 격리가 적용되지 않아 모든 권역의 데이터를 보고 관리할 수 있습니다. 로그인하면 자동으로 플랫폼 대시보드(/dashboard/platform)로 이동합니다.</Lead>
        <Steps items={[
          "발급받은 시스템 관리자 계정으로 로그인합니다.",
          "좌측 플랫폼 사이드바에서 플랫폼 대시보드·권역 관리·관리자·감사 로그·승인·기관 마스터·시스템 설정 화면으로 이동합니다.",
          "권한 매트릭스와 무관하게 모든 기능에 접근할 수 있으므로, 행위마다 책임이 따릅니다(모든 작업은 감사 로그에 기록).",
          "도움이 필요할 때는 플랫폼 대시보드 우측 상단의 '사용자 매뉴얼' 버튼으로 언제든 이 화면에 돌아옵니다.",
        ]} />
        <Lead>플랫폼 구성 흐름 — 시스템 관리자가 플랫폼을 세팅하며 거치는 핵심 화면 순서입니다. 썸네일을 클릭하면 원본 화면이 열립니다.</Lead>
        <FlowStrip steps={[
          { src: S("regions"),       label: "권역 관리" },
          { src: S("organizations"), label: "기관 마스터" },
          { src: S("managers"),      label: "케어매니저" },
          { src: S("admins"),        label: "관리자·권한" },
        ]} />
        <GoTo links={[{ href: "/dashboard/platform", label: "플랫폼 대시보드 열기" }]} />
      </Section>

      <Section id="dashboard" title="2. 플랫폼 대시보드">
        <Lead>전국 권역의 운영 지표를 한눈에 봅니다. 권역 수·전체 케이스·매칭·방문 추세 등 플랫폼 단위 현황이 표시됩니다.</Lead>
        <Steps items={[
          "상단 지표에서 전국 권역 수와 핵심 KPI를 확인합니다.",
          "권역별 분포에서 특정 권역을 선택하면 해당 권역 대시보드로 드릴다운할 수 있습니다.",
          "권역 관리자와 달리 ?code= 파라미터로 임의 권역을 열어볼 수 있습니다(전역 권한).",
        ]} />
        <Note>플랫폼 대시보드·사이드바와 관리자·감사·설정 화면은 모두 시스템 관리자 전용입니다.</Note>
        <Shot src={S("dashboard-platform")} alt="플랫폼 대시보드 화면" caption="플랫폼 대시보드 — 전국 권역 지표·권역별 분포·시스템 헬스를 한눈에 봅니다. (클릭하면 원본 크기로 열림)" />
        <GoTo links={[{ href: "/dashboard/platform", label: "플랫폼 대시보드 열기" }]} />
      </Section>

      <Section id="regions" title="3. 권역 관리">
        <Lead>권역(region) 마스터를 생성·수정·삭제하고 운영합니다. 권역 관리자는 조회만 가능하지만 시스템 관리자는 전체 CRUD 권한을 가집니다.</Lead>
        <Steps items={[
          "권역 관리 화면에서 전국 권역 목록과 통계를 봅니다.",
          "신규 권역을 등록하거나 기존 권역의 정보를 수정합니다.",
          "시범 권역을 정식으로 승격(promote)하거나, 중복 권역을 다른 권역으로 병합(merge)합니다.",
          "필요 시 권역 통계를 재계산하고, 목록을 CSV로 내보냅니다.",
        ]} />
        <Note>권역 삭제·병합은 하위 케이스·기관·매니저에 영향을 주는 <b>되돌리기 어려운 작업</b>입니다. 실행 전 영향 범위를 확인하세요(감사 로그에 region_delete/region_merge로 기록).</Note>
        <Shot src={S("regions")} alt="권역 관리 화면" caption="권역 관리 — 전국 권역 목록·통계를 보고 등록·수정·승격·병합합니다." />
        <GoTo links={[{ href: "/regions", label: "권역 관리 열기" }]} />
      </Section>

      <Section id="orgs" title="4. 기관 마스터">
        <Lead>유관기관(병원·보건소·행정복지센터 등) 마스터를 관리합니다. 시스템 관리자는 생성·수정·삭제·병합·일괄처리 권한을 가집니다.</Lead>
        <Steps items={[
          "기관 마스터 화면에서 전체 기관 목록을 조회·검색합니다.",
          "신규 기관을 등록하거나 기관 정보를 수정합니다.",
          "중복 기관을 병합하거나, 여러 기관을 일괄 수정·삭제합니다.",
          "기관 목록을 CSV로 내보낼 수 있습니다.",
        ]} />
        <Note>기관 삭제·병합·일괄처리는 광범위한 영향을 주므로 신중히 실행하세요(감사 로그에 org_delete/org_merge로 기록).</Note>
        <Shot src={S("organizations")} alt="기관 마스터 화면" caption="기관 마스터 — 전체 유관기관을 조회·검색하고 등록·수정·병합·일괄처리합니다." />
        <GoTo links={[{ href: "/organizations", label: "기관 마스터 열기" }]} />
      </Section>

      <Section id="managers" title="5. 케어매니저 관리">
        <Lead>케어매니저 계정을 생성·수정·삭제합니다. 매니저 계정 생성은 시스템 관리자(can_manage_managers) 권한입니다.</Lead>
        <Steps items={[
          "케어매니저 화면에서 전체 매니저 목록과 자격·소속을 조회합니다.",
          "신규 매니저를 등록하거나 자격·소속 기관·권역 정보를 수정합니다.",
          "퇴직 등으로 비활성화가 필요한 매니저 계정을 정리합니다.",
          "매니저 상세에서 매칭 단계 케이스를 '직접 배정'(AI 점수 우회)할 수 있습니다 — 사유 필수·감사 기록(9장 참고).",
          "매니저 목록을 CSV로 내보냅니다.",
        ]} />
        <Shot src={S("managers")} alt="케어매니저 관리 화면" caption="케어매니저 — 전체 매니저의 자격·소속을 조회하고 등록·수정·정리합니다." />
        <GoTo links={[{ href: "/managers", label: "케어매니저 관리 열기" }]} />
      </Section>

      <Section id="admins" title="6. 관리자 관리">
        <Lead>운영 관리자 계정을 초대·관리합니다. 이 화면은 시스템 관리자 전용입니다(require_system_admin!).</Lead>
        <Steps items={[
          "관리자 화면에서 관리자 목록을 역할·상태·권역·이름으로 필터링해 조회합니다.",
          "신규 관리자를 초대합니다(상태=초대됨, 임시 비밀번호 메일 자동 발송).",
          "역할·소속 권역·상태를 수정하거나, 비밀번호 재설정·계정 잠금 해제·초대 재발송을 수행합니다.",
          "관리자 목록을 CSV로 내보냅니다. 모든 작업(초대·권한 변경·잠금 해제 등)은 감사 로그에 기록됩니다.",
        ]} />
        <Note><b>마지막 활성 시스템 관리자</b>는 스스로를 강등하거나 비활성화할 수 없도록 차단됩니다(플랫폼 잠김 방지). 또한 권역 매니저의 역할을 바꾸면 기존 권역 연결이 자동 해제됩니다.</Note>
        <Shot src={S("admins")} alt="관리자 관리 화면" caption="관리자 — 관리자 계정을 초대·필터링하고, 하단 권한 정책으로 역할별 접근 범위를 봅니다." />
        <GoTo links={[{ href: "/admins", label: "관리자 관리 열기" }]} />
      </Section>

      <Section id="members" title="6-1. 회원 관리">
        <Lead>어르신·보호자 등 일반 회원의 탈퇴 처리와 재활성화를 관리합니다(can_manage_members). 시스템 관리자는 전체 회원, 권역 관리자는 자기 권역 회원만 대상입니다.</Lead>
        <Steps items={[
          "회원 관리 화면에서 어르신·보호자 회원을 검색·필터링해 조회합니다.",
          "회원을 탈퇴 처리하면 즉시 로그인이 차단되고(state=off), 유예 기간 후 개인정보가 익명화됩니다(소비자 역할만).",
          "탈퇴한 회원을 유예 기간 내 재활성화하면 로그인이 복구됩니다.",
        ]} />
        <Note>모든 탈퇴·재활성화는 감사 로그에 기록됩니다. 직원·관리자 계정은 회원 관리 대상이 아니라 관리자 화면에서 다룹니다.</Note>
        <GoTo links={[{ href: "/members", label: "회원 관리 열기" }]} />
      </Section>

      <Section id="settings" title="7. 권한 매트릭스·시스템 설정">
        <Lead>플랫폼 전역 설정과 역할 기반 권한(RBAC)을 관리합니다. 조회는 열려 있으나 변경(PATCH)은 시스템 관리자 전용입니다.</Lead>
        <Steps items={[
          "시스템 설정 화면에서 7개 섹션(일반·AI 매칭·알림·보안·데이터·연동·권한)을 봅니다.",
          "각 섹션의 값을 수정하면 라운드트립으로 저장됩니다(문자열·정수·JSON 타입 지원).",
          "'AI 매칭' 섹션에서 6개 차원 가중치(거리·자격·가용·부하·과거관계·평가, 기본 25·20·15·15·15·10%)를 조정합니다. 저장하면 COD '후보 재생성' 채점에 즉시 반영됩니다.",
          "권한 탭에서 capability별 허용 역할을 추가·제거해 권한 매트릭스를 직접 편집합니다.",
          "변경은 Setting.role_can?에 즉시 반영되어, 이후 각 사용자의 capabilities 로드 시 적용됩니다.",
        ]} />
        <Note><b>AI 매칭 가중치는 더 이상 표시용이 아니라 매칭 엔진(Matches::Regenerator)에 라이브 연동</b>됩니다. 차원별 점수의 출처는 — 거리=위치 핀 좌표, 자격=어르신 필요↔자격 적합(need-aware), 가용=주간 근무시간(취소 제외), 부하=담당 케이스 수, 과거관계=실제 방문·케이스 이력, 평가=보호자·어르신 실제 별점입니다.</Note>
        <Note>권한 매트릭스 변경은 전 사용자의 접근 범위를 바꾸는 민감 작업입니다(감사 로그에 permission_change/setting_change로 기록). 시스템 관리자는 매트릭스와 무관하게 항상 모든 권한을 가집니다(슈퍼유저). 동일 값으로의 변경은 감사 로그를 남기지 않습니다(멱등).</Note>
        <Shot src={S("settings")} alt="시스템 설정·권한 매트릭스 화면" caption="시스템 설정 — 7개 섹션과 역할 기반 권한(RBAC) 매트릭스를 편집합니다." />
        <GoTo links={[{ href: "/settings", label: "시스템 설정 열기" }]} />
      </Section>

      <Section id="audit" title="8. 감사 로그">
        <Lead>플랫폼의 모든 주요 행위를 추적·감사합니다. 이 화면은 시스템 관리자 전용입니다.</Lead>
        <Steps items={[
          "감사 로그 화면에서 전체·오늘·경고·로그인 실패 KPI를 확인합니다.",
          "액션 종류·역할·심각도(info/warn)·기간·텍스트 검색으로 필터링합니다.",
          "필터를 적용한 결과를 CSV로 내보냅니다(최대 10,000행).",
          "알림 규칙을 생성·수정·삭제해 특정 감사 이벤트에 대한 알림을 운영합니다.",
        ]} />
        <Note>로그인 실패·권한 변경·설정 변경·계정 잠금·권역/기관 삭제·CSV 내보내기 등은 <b>경고(warn)</b>로 분류되어 우선 모니터링 대상입니다.</Note>
        <Shot src={S("audit")} alt="감사 로그 화면" caption="감사 로그 — KPI와 액션·역할·심각도·기간 필터로 전 행위를 추적하고 CSV로 내보냅니다." />
        <GoTo links={[{ href: "/audit", label: "감사 로그 열기" }]} />
      </Section>

      <Section id="ops" title="9. 승인·매칭 운영">
        <Lead>시스템 관리자도 케어 신청 승인과 AI 매칭 결정에 참여할 수 있습니다. 전역 권한이라 모든 권역의 건을 처리합니다.</Lead>
        <Steps items={[
          "승인 관리 화면에서 전 권역의 케어 신청을 승인·반려합니다(공유 승인 큐).",
          "필요 시 AI 자동매칭(COD) 화면에서 6개 차원(거리·자격·가용·부하·과거관계·평가) 점수와 설명(XAI)으로 후보를 비교·결정합니다. 가중치 기본값은 시스템 설정(7장)에서 바꿉니다.",
          "운영 화면(일정·지도·케이스/방문 상세)은 권역 관리자와 동일하게 사용하되, 범위가 전역입니다.",
        ]} />
        <Note>승인 큐는 승인자 풀(시스템/플랫폼/권역 관리자·권역 운영자)이 함께 처리하는 전 권역 공유 큐입니다.</Note>
        <Note><b>직접 배정(점수 우회):</b> 부득이하게 AI 점수를 우회해 특정 매니저에게 케이스를 직접 배정하려면 케어매니저 관리(5장) 화면의 '직접 배정'을 사용합니다(can_manage_cases). <b>배정 사유 입력이 필수</b>이며 감사 로그(case_assigned_direct · 5년 보존)에 남습니다. 기본 배정 경로는 점수·근거 기반의 AI 자동매칭입니다.</Note>
        <Shot src={S("approval")} alt="승인 관리 화면" caption="승인 관리 — 전 권역 공유 큐에서 케어 신청을 승인·반려합니다." />
        <GoTo links={[{ href: "/approval", label: "승인 관리" }, { href: "/cod", label: "AI 자동매칭" }]} />
      </Section>

      <Section id="security" title="10. 보안·운영 주의">
        <Lead>전역 슈퍼유저인 만큼 다음 사항을 반드시 유의하세요.</Lead>
        <p style={{ margin: "12px 0 6px", fontSize: 13, fontWeight: 800, color: "var(--orange-700)" }}>시스템 관리자 전용 화면</p>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.8 }}>
          <li>관리자 관리(/admins) · 감사 로그(/audit) · 시스템 설정·권한 매트릭스(/settings)</li>
          <li>권역·기관·매니저 마스터의 생성·수정·삭제·병합 (권역 관리자는 조회만)</li>
        </ul>
        <p style={{ margin: "12px 0 6px", fontSize: 13, fontWeight: 800, color: "var(--green-600, #2F8F5B)" }}>운영 수칙</p>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.8 }}>
          <li>계정·비밀번호를 안전하게 관리합니다.</li>
          <li>권역/기관 삭제·병합, 권한 매트릭스 변경 등 되돌리기 어려운 작업은 영향 범위를 확인 후 실행합니다.</li>
          <li>모든 주요 행위는 감사 로그에 남으므로, 정기적으로 경고·로그인 실패를 모니터링합니다.</li>
        </ul>
        <Note>마지막 활성 시스템 관리자는 자기강등·비활성화가 차단됩니다. 관리자 인수인계 시 새 시스템 관리자를 먼저 활성화한 뒤 기존 계정을 정리하세요.</Note>
      </Section>

      <Section id="faq" title="11. 자주 묻는 질문 · 문의">
        <p style={{ margin: "10px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13.5 }}>Q. 권한 매트릭스를 바꿨는데 사용자에게 언제 반영되나요?</p>
        <Lead>변경 즉시 Setting.role_can?에 반영되며, 각 사용자는 다음 capabilities 로드(세션 갱신) 시 새 권한을 적용받습니다.</Lead>
        <p style={{ margin: "14px 0 4px", fontWeight: 700, color: "var(--ink-1)", fontSize: 13.5 }}>Q. 실수로 마지막 관리자를 강등할까 걱정됩니다.</p>
        <Lead>마지막 활성 시스템 관리자의 자기강등·비활성화는 시스템이 자동으로 차단합니다. 안심하고 인수인계 절차를 따르세요.</Lead>
        <div style={{ marginTop: 18, padding: 16, background: "var(--bg-sub)", borderRadius: 12, fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.8 }}>
          <div style={{ fontWeight: 800, color: "var(--ink-1)", marginBottom: 4 }}>운영지원 문의</div>
          support@care.local · {supportPhone()} (평일 09:00–18:00)
        </div>
      </Section>
    </ManualShell>
  );
}

export { SystemManual };
